/**
 * /generations — the extended-family hub. Single circle at a time, with
 * Photos / Prayer / Chat sub-surfaces driven by the existing endpoints'
 * ?circleId=X query. Compose flows pass `audience: { type: 'circle',
 * circleId }` so posts have exactly one audience going forward.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type Prayer } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useMentionRoster, useMyCircles, type Circle } from '../lib/circles';
import { MentionTextarea, renderWithMentions } from '../components/MentionTextarea';
import { ScreenHeader } from '../components/ScreenHeader';
import { Section } from '../components/Section';
import { Button } from '../components/Button';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { IconButton } from '../components/IconButton';
import { IconImage, IconTrash } from '../components/icons';
import { uploadPhoto } from '../lib/photo-upload';

export default function GenerationsPage() {
  const circles = useMyCircles();
  const [activeCircleId, setActiveCircleId] = useState<string | null>(null);
  const [surface, setSurface] = useState<'photos' | 'prayer' | 'chat'>('photos');

  // Default to the first circle once they load. Re-runs if the user
  // joins/leaves circles and the active id falls out of the list.
  useEffect(() => {
    const list = circles.data?.circles ?? [];
    if (list.length === 0) {
      if (activeCircleId !== null) setActiveCircleId(null);
      return;
    }
    if (!activeCircleId || !list.some((c) => c.id === activeCircleId)) {
      setActiveCircleId(list[0]!.id);
    }
  }, [circles.data, activeCircleId]);

  if (circles.isLoading) {
    return (
      <>
        <ScreenHeader title="Generations" />
        <div className="px-5 pt-4">
          <p className="text-soft text-sm">Loading…</p>
        </div>
      </>
    );
  }

  const list = circles.data?.circles ?? [];
  const active = list.find((c) => c.id === activeCircleId) ?? null;

  return (
    <>
      <ScreenHeader
        title="Generations"
        subtitle="Extended family — what we share with grandparents, cousins, the church."
        right={
          <Link to="/generations/manage" className="text-oxblood text-sm underline">
            Manage
          </Link>
        }
      />
      <div className="px-5 pt-4 pb-6">
        {list.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <CircleSwitcher
              circles={list}
              active={activeCircleId}
              onChange={setActiveCircleId}
            />
            {active && (
              <SurfaceTabs circle={active} active={surface} onChange={setSurface} />
            )}
            {active && surface === 'photos' && <CirclePhotos circle={active} />}
            {active && surface === 'prayer' && <CirclePrayers circle={active} />}
            {active && surface === 'chat' && <CircleChat circle={active} />}
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- empty state

function EmptyState() {
  return (
    <div className="text-center py-12">
      <h2 className="font-serif text-2xl text-ink">No circles yet</h2>
      <p className="text-soft mt-2 max-w-sm mx-auto">
        Start one with grandparents or cousins, or join an existing one with an invite
        code. Each circle has its own Photos / Prayer / Chat — surfaces are opt-in per
        circle.
      </p>
      <Link
        to="/generations/manage"
        className="inline-flex mt-5 h-11 px-5 items-center rounded-xl bg-oxblood text-paper text-sm font-medium"
      >
        Manage circles
      </Link>
    </div>
  );
}

// ----------------------------------------------------------- circle switcher

function CircleSwitcher({
  circles,
  active,
  onChange,
}: {
  circles: Circle[];
  active: string | null;
  onChange: (id: string) => void;
}) {
  if (circles.length === 1) {
    // One circle: show its name as a static label, no pills.
    return (
      <div className="mb-4">
        <span className="kicker text-soft">Circle</span>
        <h2 className="font-serif text-xl text-ink">{circles[0]!.name}</h2>
      </div>
    );
  }
  return (
    <div className="mb-4 -mx-1 flex gap-2 overflow-x-auto pb-2">
      {circles.map((c) => {
        const isActive = c.id === active;
        return (
          <button
            key={c.id}
            onClick={() => onChange(c.id)}
            className={`shrink-0 px-4 h-9 rounded-full text-sm font-medium border ${
              isActive ? 'bg-ink text-paper border-ink' : 'text-soft border-line bg-paper'
            }`}
          >
            {c.name}
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------- surface tabs

function SurfaceTabs({
  circle,
  active,
  onChange,
}: {
  circle: Circle;
  active: 'photos' | 'prayer' | 'chat';
  onChange: (s: 'photos' | 'prayer' | 'chat') => void;
}) {
  // Only surfaces the circle actually has enabled show up.
  const items: { key: 'photos' | 'prayer' | 'chat'; label: string }[] = [];
  if (circle.surfaces.photos) items.push({ key: 'photos', label: 'Photos' });
  if (circle.surfaces.prayer) items.push({ key: 'prayer', label: 'Prayer' });
  if (circle.surfaces.chat) items.push({ key: 'chat', label: 'Chat' });

  // If the previously-active surface isn't enabled here, snap to the
  // first available one. This effect runs above the early-return so
  // hook count stays stable across the "all surfaces off" case — the
  // previous order had this effect below the no-surfaces guard, which
  // changed the hook count whenever a circle toggled to/from zero
  // enabled surfaces.
  const firstKey = items[0]?.key ?? null;
  const hasActive = items.some((i) => i.key === active);
  useEffect(() => {
    if (firstKey && !hasActive) onChange(firstKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [circle.id, firstKey, hasActive]);

  if (items.length === 0) {
    return (
      <p className="text-soft italic text-sm py-3">
        No surfaces enabled for this circle. Toggle one on under{' '}
        <Link to="/generations/manage" className="underline text-oxblood">
          Manage
        </Link>
        .
      </p>
    );
  }

  return (
    <div className="inline-flex rounded-full border border-line p-1 bg-paper mb-4">
      {items.map((i) => (
        <button
          key={i.key}
          onClick={() => onChange(i.key)}
          className={`px-4 h-9 rounded-full text-sm font-medium ${
            active === i.key ? 'bg-ink text-paper' : 'text-muted'
          }`}
        >
          {i.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- photos

interface CirclePhoto {
  id: string;
  url: string;
  takenAt: number | null;
  width: number | null;
  height: number | null;
}

function CirclePhotos({ circle }: { circle: Circle }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const gridKey = ['photos-grid', 'circle', circle.id] as const;
  const grid = useQuery({
    queryKey: gridKey,
    queryFn: () =>
      api.get<{ photos: CirclePhoto[]; nextCursor: number | null }>(
        `/photos/grid?circleId=${encodeURIComponent(circle.id)}`,
      ),
  });

  // Track in-flight uploads as optimistic tiles so the grid feels
  // responsive. Each entry holds the object URL we'll revoke after
  // the server confirms.
  const [pending, setPending] = useState<{ id: string; url: string }[]>([]);
  // Banner messages — errors (oxblood) and dedup notices (soft).
  // Cleared on next upload batch start, or dismissed by tap.
  const [notice, setNotice] = useState<{
    kind: 'error' | 'info';
    text: string;
  } | null>(null);

  async function onFilesPicked(files: File[]) {
    if (files.length === 0) return;
    setNotice(null);
    const tiles = files.map((file) => ({
      id: `tmp_${crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)}`,
      url: URL.createObjectURL(file),
      file,
    }));
    setPending((prev) => [...tiles.map((t) => ({ id: t.id, url: t.url })), ...prev]);

    const beforeCount =
      qc.getQueryData<{ photos: CirclePhoto[] }>(gridKey)?.photos.length ?? 0;
    let uploadedCount = 0;
    let alreadyCount = 0;
    let dedupedCount = 0;
    const errors: string[] = [];

    // Upload up to 3 in parallel, then drop each tile as it completes.
    const queue = [...tiles];
    const workers = Array.from({ length: Math.min(3, queue.length) }, async () => {
      while (queue.length) {
        const t = queue.shift();
        if (!t) return;
        try {
          const result = await uploadPhoto(t.file, {
            type: 'circle',
            circleId: circle.id,
          });
          if (result.kind === 'deduped') {
            if (result.alreadyShared) alreadyCount += 1;
            else dedupedCount += 1;
          } else {
            uploadedCount += 1;
          }
        } catch (e) {
          const msg = e instanceof ApiError ? e.message : (e as Error).message;
          // Log raw error to console too so the dev tools have the stack.
          // eslint-disable-next-line no-console
          console.error('circle upload failed:', t.file.name, e);
          errors.push(`${t.file.name}: ${msg}`);
        } finally {
          URL.revokeObjectURL(t.url);
          setPending((prev) => prev.filter((p) => p.id !== t.id));
        }
      }
    });
    await Promise.all(workers);

    // Refresh the grid once at the end (one invalidation, not N).
    // refetchQueries (not just invalidate) so the await resolves only
    // after the refetch lands — lets us assert the new photos actually
    // showed up before reporting success.
    const refreshed = await qc
      .refetchQueries({ queryKey: gridKey })
      .then(() => qc.getQueryData<{ photos: CirclePhoto[] }>(gridKey)?.photos.length ?? 0)
      .catch(() => null);

    // Surface the outcome. Errors take priority.
    if (errors.length > 0) {
      setNotice({
        kind: 'error',
        text:
          errors.length === 1
            ? `Couldn't share: ${errors[0]}`
            : `Couldn't share ${errors.length} photos. First: ${errors[0]}`,
      });
      return;
    }
    const bits: string[] = [];
    if (uploadedCount > 0) {
      bits.push(
        `Shared ${uploadedCount} ${uploadedCount === 1 ? 'photo' : 'photos'} to ${circle.name}`,
      );
    }
    if (dedupedCount > 0) {
      bits.push(`${dedupedCount} already in your library — linked into ${circle.name}`);
    }
    if (alreadyCount > 0) {
      bits.push(`${alreadyCount} already shared with ${circle.name}`);
    }
    if (bits.length > 0) {
      setNotice({ kind: 'info', text: bits.join(' · ') });
    }

    // Sanity: if the server said upload succeeded but the refreshed grid
    // didn't grow, surface that loudly. Common causes:
    //   - deployed API missing ?circleId support on /photos/grid (Phase B
    //     not deployed → grid scoped to family, not circle)
    //   - photo_circles M2M insert silently failed (FK or PK conflict)
    //   - migration 0010 didn't apply (hash column missing → presign
    //     errors out earlier, but that would land in `errors` above)
    // Don't overwrite an existing success/info notice — append.
    if (
      uploadedCount > 0 &&
      refreshed !== null &&
      refreshed - beforeCount < uploadedCount
    ) {
      setNotice({
        kind: 'error',
        text:
          `Upload succeeded but the photo isn't appearing in this Generation. ` +
          `The Worker may not have the latest deploy — try a full pnpm ship ` +
          `and refresh. Open DevTools → Network to see the /photos/finalize and ` +
          `/photos/grid responses.`,
      });
    }
  }

  if (grid.isLoading && pending.length === 0) {
    return <p className="text-soft text-sm">Loading photos…</p>;
  }
  if (grid.error instanceof ApiError) {
    return <p className="text-oxblood text-sm">{grid.error.message}</p>;
  }

  const photos = grid.data?.photos ?? [];
  const empty = photos.length === 0 && pending.length === 0;

  return (
    <Section>
      <div className="flex justify-end mb-3">
        <IconButton
          icon={<IconImage />}
          label="Share photo"
          onClick={() => inputRef.current?.click()}
        />
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            // Snapshot the File objects into a real array BEFORE we
            // reset the input value. e.target.files is a live FileList:
            // clearing the input wipes the captured reference too, so
            // any later access lands on an empty list. Memories does
            // the same snapshot for the same reason.
            const files = Array.from(e.target.files ?? []);
            if (e.target) e.target.value = '';
            void onFilesPicked(files);
          }}
        />
      </div>

      {notice && (
        <button
          onClick={() => setNotice(null)}
          className={`block w-full text-left mb-3 px-3 py-2 rounded-lg text-sm ${
            notice.kind === 'error'
              ? 'bg-oxblood/10 text-oxblood'
              : 'bg-sage/10 text-sage'
          }`}
          aria-label="Dismiss notice"
        >
          {notice.text}
        </button>
      )}

      {empty ? (
        <p className="text-muted italic py-8 text-center">
          No photos shared with {circle.name} yet. Tap the photo button above to add some.
        </p>
      ) : (
        <div className="grid grid-cols-3 gap-2">
          {pending.map((p) => (
            <div
              key={p.id}
              className="aspect-square w-full overflow-hidden rounded-lg bg-line relative"
              aria-label="Uploading…"
            >
              <img src={p.url} alt="" className="w-full h-full object-cover opacity-60" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 rounded-full border-2 border-paper border-t-oxblood animate-spin" />
              </div>
            </div>
          ))}
          {photos.map((p) => (
            <Link
              key={p.id}
              to={`/memories/${p.id}?circleId=${encodeURIComponent(circle.id)}`}
              className="aspect-square w-full overflow-hidden rounded-lg bg-line"
            >
              <img
                src={p.url}
                alt=""
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </Link>
          ))}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- prayers

function CirclePrayers({ circle }: { circle: Circle }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [draftMentions, setDraftMentions] = useState<string[]>([]);

  const list = useQuery({
    queryKey: ['prayers', 'circle', circle.id],
    queryFn: () =>
      api.get<{ prayers: Prayer[] }>(
        `/prayers?status=active&circleId=${encodeURIComponent(circle.id)}`,
      ),
  });

  const create = useMutation({
    mutationFn: (args: { body: string; mentions: string[] }) =>
      api.post<{ prayer: Prayer }>('/prayers', {
        ...args,
        audience: { type: 'circle', circleId: circle.id },
      }),
    onSuccess: () => {
      setDraft('');
      setDraftMentions([]);
      void qc.invalidateQueries({ queryKey: ['prayers', 'circle', circle.id] });
    },
  });

  // Merged @-mention roster: household + this circle's members. Lets
  // the picker offer cross-household tagging on circle prayers.
  const mentionRoster = useMentionRoster(circle.id);

  return (
    <Section>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim())
            create.mutate({ body: draft.trim(), mentions: draftMentions });
        }}
        className="rounded-xl border border-line bg-paper p-3"
      >
        <MentionTextarea
          value={draft}
          onChange={(v, m) => {
            setDraft(v);
            setDraftMentions(m);
          }}
          members={mentionRoster}
          rows={3}
          placeholder={`Share a request with ${circle.name}…`}
          className="w-full bg-transparent text-ink placeholder:text-muted resize-none focus:outline-none"
        />
        <div className="flex justify-end">
          <Button
            type="submit"
            size="sm"
            disabled={!draft.trim()}
            busy={create.isPending}
          >
            Post prayer
          </Button>
        </div>
      </form>

      {list.isLoading && <p className="mt-4 text-soft text-sm">Loading…</p>}
      {list.error instanceof ApiError && (
        <p className="mt-4 text-oxblood text-sm">{list.error.message}</p>
      )}
      <div className="mt-4 divide-y divide-line">
        {list.data?.prayers.map((p) => (
          <article key={p.id} className="py-4 first:pt-0 last:pb-0">
            <p className="text-ink leading-relaxed">{renderWithMentions(p.body)}</p>
            <div className="text-xs text-muted mt-3">
              {new Date(p.createdAt).toLocaleString([], {
                month: 'short',
                day: 'numeric',
              })}
            </div>
          </article>
        ))}
        {list.data?.prayers.length === 0 && (
          <p className="text-muted text-center py-4 italic">
            No active requests shared with this circle.
          </p>
        )}
      </div>
    </Section>
  );
}

// --------------------------------------------------------------- circle chat
// REST-only v1 — circle chat doesn't use the family ChatRoom Durable
// Object (which is keyed by family_id). Send + invalidate the history
// query. Live updates can come later if circles see real traffic.

interface CircleMessage {
  id: string;
  authorId: string;
  kind: string;
  body: { kind: string; text?: string };
  createdAt: number;
  reactions?: { memberId: string; kind: string }[];
}

function CircleChat({ circle }: { circle: Circle }) {
  const qc = useQueryClient();
  const auth = useAuth();
  const meId = auth.state === 'signed-in' ? auth.me.member.id : null;
  const [draft, setDraft] = useState('');
  const [draftMentions, setDraftMentions] = useState<string[]>([]);
  const mentionRoster = useMentionRoster(circle.id);

  const history = useQuery({
    queryKey: ['chat-history', 'circle', circle.id],
    queryFn: () =>
      api.get<{ messages: CircleMessage[] }>(
        `/chat/history?circleId=${encodeURIComponent(circle.id)}`,
      ),
  });

  const send = useMutation({
    mutationFn: (args: { text: string; mentions: string[] }) =>
      api.post('/chat/send', {
        body: { kind: 'text', text: args.text, mentions: args.mentions },
        audience: { type: 'circle', circleId: circle.id },
      }),
    onSuccess: () => {
      setDraft('');
      setDraftMentions([]);
      void qc.invalidateQueries({ queryKey: ['chat-history', 'circle', circle.id] });
    },
  });

  // Toggle a reaction. The server endpoints idempotent-toggle via
  // ON CONFLICT DO NOTHING (POST) / DELETE; we don't bother with
  // optimistic updates here because the circle history isn't yet
  // realtime — invalidating refetches and renders the new state.
  const react = useMutation({
    mutationFn: async (args: { messageId: string; kind: 'praying' | 'heart' }) => {
      const url = `/chat/messages/${args.messageId}/react?kind=${args.kind}`;
      // Check whether the caller already reacted with this kind to
      // decide POST vs DELETE — keep a single tap = toggle.
      const cached = qc.getQueryData<{ messages: CircleMessage[] }>([
        'chat-history',
        'circle',
        circle.id,
      ]);
      const msg = cached?.messages.find((m) => m.id === args.messageId);
      const alreadyOn = msg?.reactions?.some(
        (r) => r.memberId === meId && r.kind === args.kind,
      );
      if (alreadyOn) await api.delete(url);
      else await api.post(url);
    },
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: ['chat-history', 'circle', circle.id] }),
  });

  const deleteMessage = useMutation({
    mutationFn: (id: string) => api.delete(`/chat/messages/${id}`),
    onSuccess: () => {
      setPendingDeleteMsg(null);
      void qc.invalidateQueries({ queryKey: ['chat-history', 'circle', circle.id] });
    },
  });
  const [pendingDeleteMsg, setPendingDeleteMsg] = useState<string | null>(null);

  // Reverse to oldest-first so the compose row sits below the latest
  // message — matches /chat's reading order.
  const ordered = (history.data?.messages ?? []).slice().reverse();

  return (
    <Section>
      {history.isLoading && <p className="text-soft text-sm">Loading…</p>}
      <div className="divide-y divide-line">
        {ordered.map((m) => {
          const prayingCount =
            m.reactions?.filter((r) => r.kind === 'praying').length ?? 0;
          const heartCount = m.reactions?.filter((r) => r.kind === 'heart').length ?? 0;
          const iPrayed = !!m.reactions?.some(
            (r) => r.memberId === meId && r.kind === 'praying',
          );
          const iHearted = !!m.reactions?.some(
            (r) => r.memberId === meId && r.kind === 'heart',
          );
          const mine = m.authorId === meId;
          return (
            <article key={m.id} className="py-3 first:pt-0 last:pb-0">
              <p className="text-ink">
                {m.body.text ? renderWithMentions(m.body.text) : `[${m.kind}]`}
              </p>
              <div className="flex items-center justify-between mt-2 gap-3">
                <div className="text-xs text-muted">
                  {new Date(m.createdAt).toLocaleString([], {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                  })}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => react.mutate({ messageId: m.id, kind: 'praying' })}
                    aria-pressed={iPrayed}
                    className={`text-xs px-2 h-7 rounded-full border ${
                      iPrayed
                        ? 'bg-sage/15 border-sage text-sage'
                        : 'border-line text-soft'
                    }`}
                  >
                    🙏 {prayingCount > 0 ? prayingCount : ''}
                  </button>
                  <button
                    type="button"
                    onClick={() => react.mutate({ messageId: m.id, kind: 'heart' })}
                    aria-pressed={iHearted}
                    className={`text-xs px-2 h-7 rounded-full border ${
                      iHearted
                        ? 'bg-oxblood/10 border-oxblood text-oxblood'
                        : 'border-line text-soft'
                    }`}
                  >
                    ♡ {heartCount > 0 ? heartCount : ''}
                  </button>
                  {mine && (
                    <IconButton
                      icon={<IconTrash />}
                      label="Delete message"
                      variant="destructive"
                      onClick={() => setPendingDeleteMsg(m.id)}
                    />
                  )}
                </div>
              </div>
            </article>
          );
        })}
        {ordered.length === 0 && !history.isLoading && (
          <p className="text-muted text-center py-4 italic">Quiet here. Say hello.</p>
        )}
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (draft.trim()) send.mutate({ text: draft.trim(), mentions: draftMentions });
        }}
        className="mt-4 flex gap-2 items-start"
      >
        <div className="flex-1 min-w-0">
          <MentionTextarea
            value={draft}
            onChange={(v, m) => {
              setDraft(v);
              setDraftMentions(m);
            }}
            members={mentionRoster}
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter inserts a newline. Matches the
              // family chat composer's behavior.
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (draft.trim())
                  send.mutate({ text: draft.trim(), mentions: draftMentions });
              }
            }}
            rows={1}
            placeholder={`Message ${circle.name}…`}
            className="w-full px-4 py-2.5 rounded-2xl border border-line bg-paper italic text-ink resize-none"
          />
        </div>
        <Button
          type="submit"
          disabled={!draft.trim()}
          busy={send.isPending}
          className="shrink-0"
        >
          Send
        </Button>
      </form>

      {pendingDeleteMsg && (
        <ConfirmDialog
          title="Delete this message?"
          message="It will be removed for everyone in the circle."
          confirmLabel="Delete"
          destructive
          busy={deleteMessage.isPending}
          error={deleteMessage.isError ? 'Could not delete. Try again.' : null}
          onConfirm={() => deleteMessage.mutate(pendingDeleteMsg)}
          onCancel={() => setPendingDeleteMsg(null)}
        />
      )}
    </Section>
  );
}
